<template>
  <main class="app-content">
    <div class="app-title">
      <div>
        <h1>
          <font-awesome-icon icon="file-signature" />
          {{ $t('kycRequest.title') }}
        </h1>
      </div>
    </div>
    <div class="row">
      <!-- Status tabs (lên trên) -->
      <div class="col-12">
        <ul class="nav nav-tabs kyc-status-tabs">
          <li class="nav-item" v-for="tab in statusTabs" :key="tab.value || 'all'">
            <a class="nav-link" href="#"
               :class="{active: filterFormModel.status === tab.value}"
               @click.prevent="changeStatus(tab.value)">
              <font-awesome-icon :icon="tab.icon" v-if="tab.icon" />
              {{ tab.label }}
              <span class="badge ml-1" :class="filterFormModel.status === tab.value ? 'badge-light' : 'badge-secondary'">
                {{ tabCount(tab.value) }}
              </span>
            </a>
          </li>
        </ul>
      </div>

      <!-- Filters -->
      <div class="col-12">
        <div class="tile">
          <div class="form-inline">
            <b-form-input class="mr-1 mb-1" style="min-width: 200px;"
                          v-model="filterFormModel.q" @keydown.enter="load"
                          :placeholder="$t('keyword')" />
            <client-select class="mr-1 mb-1" :model="filterFormModel" attribute="client" @change="load" />
            <form-select class="mr-1 mb-1" :model="filterFormModel" attribute="contractType"
                         :options="contractTypeOptions" @change="load" />
            <form-select class="mr-1 mb-1" :model="filterFormModel" attribute="priority"
                         :options="priorityOptions" @change="load" />
            <admin-user-select class="mr-1 mb-1" :model="filterFormModel" attribute="assignedTo"
                               :is-filter="true" :place-holder="$t('kycRequest.field.assignedTo')"
                               not-updated-select />
            <admin-user-select class="mr-1 mb-1" :model="filterFormModel" attribute="requestedBy"
                               :is-filter="true" :place-holder="$t('kycRequest.field.requestedBy')"
                               not-updated-select />
            <time-range-picker :model="filterFormModel" :label="$t('createTime')" class="mr-1 mb-1" />
            <button @click="load" class="btn btn-primary mr-1 mb-1">{{ $t('search') }}</button>
            <button @click="reset" class="btn btn-secondary mr-1 mb-1">Reset</button>
          </div>
        </div>
      </div>

      <!-- Actions -->
      <div class="col-12">
        <div class="c-sorting">
          <div class="row">
            <div class="col-lg-12 c-sorting__right">
              <div class="c-sorting__btn">
                <button @click="$refs.createModal.open(null)" class="btn btn-sm btn-success"
                        v-if="canCreate">
                  <font-awesome-icon icon="plus" />&nbsp;{{ $t('kycRequest.create') }}
                </button>
              </div>
            </div>
          </div>
        </div>

        <div class="c-paging clearfix">
          <div class="c-paging__count" v-if="itemCount">{{ $t('found', [itemCount]) }}</div>
          <div class="c-paging__right">
            <div class="c-paging__numbers">
              <b-pagination v-if="itemCount > filterFormModel.pageSize" class="pull-right" size="sm"
                            v-model="filterFormModel.page" :total-rows="itemCount"
                            :per-page="filterFormModel.pageSize" @input="load" />
            </div>
          </div>
        </div>
      </div>

      <!-- List (smart-table style) -->
      <div class="col-12" v-if="items.length">
        <div class="c-smart-table tile">
          <div class="row c-smart-table__title">
            <div class="col-lg-4">{{ $t('kycRequest.clientLabel') }}</div>
            <div class="col-lg-3">{{ $t('kycRequest.field.requestedBy') }} / {{ $t('kycRequest.field.assignedTo') }}</div>
            <div class="col-lg-3">{{ $t('status') }}</div>
            <div class="col-lg-2"></div>
          </div>
          <div class="row" v-for="item in items" :key="item._id">
            <!-- CỘT 1: KHÁCH HÀNG + LOẠI -->
            <div class="col-lg-4">
              <div v-if="item.client" class="d-flex align-items-start">
                <div class="kyc-avatar mr-3 kyc-avatar--enterprise">
                  <i class="fa fa-file-signature"></i>
                </div>
                <div class="flex-grow-1 min-width-0">
                  <p class="font-weight-bold mb-1 text-truncate" :title="item.client.companyName">
                    <a href="#" @click.prevent="openDetail(item._id)">
                      {{ item.client.companyName || item.client.domain }}
                    </a>
                  </p>
                  <p class="mb-1" v-if="item.client.companyName">
                    <code class="domain-tag">{{ item.client.domain }}</code>
                  </p>
                  <p class="mb-1">
                    <span class="badge badge-light">
                      {{ $t('kycRequest.contractType.' + (item.contractType || 'unspecified')) }}
                    </span>
                    <span class="ml-1" :class="priorityBadgeClass(item.priority)">
                      {{ $t('kycRequest.priority.' + item.priority) }}
                    </span>
                  </p>
                  <p v-if="item.note" class="text-muted font-italic mb-0 note-truncate" :title="item.note">
                    <i class="fa fa-quote-left mr-1"></i>{{ item.note }}
                  </p>
                </div>
              </div>
              <div v-else class="text-secondary">[deleted]</div>
            </div>

            <!-- CỘT 2: SALES / ACCOUNT -->
            <div class="col-lg-3">
              <p class="mb-1">
                <i class="fa fa-user-tie text-muted mr-1"></i>
                <b>{{ $t('kycRequest.field.requestedBy') }}:</b>
                <span v-if="item.requestedBy">{{ item.requestedBy.name }}</span>
                <span v-else class="text-muted">—</span>
              </p>
              <p class="mb-1">
                <i class="fa fa-headset text-muted mr-1"></i>
                <b>{{ $t('kycRequest.field.assignedTo') }}:</b>
                <span v-if="item.assignedTo">{{ item.assignedTo.name }}</span>
                <span v-else class="text-warning">
                  <i class="fa fa-exclamation-triangle"></i>&nbsp;Chưa gán
                </span>
              </p>
              <p v-if="item.exportedContractNumber" class="mb-0">
                <i class="fa fa-hashtag text-muted mr-1"></i>
                <b class="text-primary">{{ item.exportedContractNumber }}</b>
              </p>
            </div>

            <!-- CỘT 3: STATUS + TIME -->
            <div class="col-lg-3">
              <p class="mb-2">
                <k-y-c-request-status-badge :status="item.status" />
              </p>
              <p class="mb-1" :class="isStale(item) ? 'text-danger' : 'text-muted'">
                <i class="fa fa-calendar-plus mr-1"></i>{{ $t('kycRequest.field.createTime') }}:
                {{ item.createTime | moment('DD/MM/YY HH:mm') }}
                <span class="ml-1 small">({{ formatTimeAgo(item.createTime) }})</span>
                <span v-if="isStale(item)" class="badge badge-danger ml-1">
                  <i class="fa fa-exclamation-triangle"></i> {{ $t('kycRequest.filter.stale') }}
                </span>
              </p>
              <p v-if="item.exportTime" class="text-success mb-1">
                <i class="fa fa-file-pdf mr-1"></i>{{ item.exportTime | moment('DD/MM/YY HH:mm') }}
              </p>
              <p v-else-if="item.cancelTime" class="text-danger mb-1">
                <i class="fa fa-times-circle mr-1"></i>{{ item.cancelTime | moment('DD/MM/YY HH:mm') }}
              </p>

              <!-- ===== MISA WeSign box (lấy từ item.kyc đã populate) ===== -->
              <div v-if="item.kyc && item.kyc.wesignAmisappRef" class="wesign-box mt-2 small">
                <div class="wesign-row wesign-header">
                  <div>
                    <font-awesome-icon icon="signature" class="text-info mr-1"/><strong>MISA WeSign</strong>
                    <span class="badge ml-1" :class="wesignBadgeClass(item.kyc.wesignStatus)">
                      {{ wesignStatusLabel(item.kyc.wesignStatus) }}
                    </span>
                  </div>
                  <div class="text-right">
                    <span class="text-muted">Khách:</span>
                    <strong :class="customerSignedCount(item.kyc) === customerParticipants(item.kyc).length ? 'text-success' : 'text-warning'">
                      {{ customerSignedCount(item.kyc) }}/{{ customerParticipants(item.kyc).length }}
                    </strong> đã ký
                  </div>
                </div>
                <div v-if="item.kyc.wesignDocumentName" class="wesign-doc-name text-truncate" :title="item.kyc.wesignDocumentName">
                  <font-awesome-icon icon="file-signature" class="text-muted mr-1"/>{{ item.kyc.wesignDocumentName }}
                </div>
                <table v-if="item.kyc.wesignParticipants && item.kyc.wesignParticipants.length"
                       class="wesign-participants">
                  <tr v-for="(p, idx) in item.kyc.wesignParticipants" :key="idx">
                    <td class="w-icon">
                      <font-awesome-icon v-if="p.action === 2" icon="check-circle" class="text-success"/>
                      <font-awesome-icon v-else-if="p.action === 3" icon="times-circle" class="text-danger"/>
                      <font-awesome-icon v-else icon="clock" class="text-warning"/>
                    </td>
                    <td class="w-name">
                      <span :class="customerActionClass(p.action)">{{ p.name }}</span>
                    </td>
                    <td class="w-role">
                      <span class="badge" :class="isInternalParticipant(p) ? 'badge-secondary' : 'badge-info'">
                        {{ isInternalParticipant(p) ? 'NV' : 'KH' }}
                      </span>
                    </td>
                    <td class="w-email text-muted text-truncate">{{ p.email }}</td>
                  </tr>
                </table>
                <div class="wesign-row wesign-footer text-muted">
                  <div>
                    <code class="mr-2">{{ item.kyc.wesignAmisappRef.substring(0, 8) }}…{{ item.kyc.wesignAmisappRef.substring(item.kyc.wesignAmisappRef.length - 4) }}</code>
                    <span v-if="item.kyc.wesignLastSyncTime">
                      <font-awesome-icon icon="sync" class="mr-1"/>{{ formatTimeAgoShort(item.kyc.wesignLastSyncTime) }}
                    </span>
                    <span v-else class="text-warning">
                      <font-awesome-icon icon="exclamation-triangle" class="mr-1"/>Chưa đồng bộ
                    </span>
                  </div>
                  <div>
                    <a :href="'https://amisapp.misa.vn/wesign/documents/waiting-for-others/' + item.kyc.wesignAmisappRef"
                       target="_blank" class="wesign-icon-btn wesign-icon-btn--info"
                       title="Mở trên MISA dashboard">
                      <font-awesome-icon icon="external-link-alt"/>
                    </a>
                    <a href="javascript:void(0)" @click="onSyncWesignKyc(item)"
                       class="wesign-icon-btn wesign-icon-btn--primary"
                       :class="{'wesign-icon-btn--disabled': wesignSyncing[item.kyc._id]}"
                       title="Đồng bộ trạng thái với MISA ngay">
                      <font-awesome-icon icon="sync" :spin="wesignSyncing[item.kyc._id] === true"/>
                    </a>
                  </div>
                </div>
              </div>
            </div>

            <!-- CỘT 4: ACTIONS -->
            <div class="col-lg-2">
              <div class="c-smart-table__content">
                <div class="c-buttons clearfix is-2">
                  <div class="c-buttons__col">
                    <button @click="openDetail(item._id)"
                            class="btn btn-sm btn-outline-info is-border-1 ml-1 mb-1">
                      <font-awesome-icon icon="folder-open" />&nbsp;{{ $t('detail') }}
                    </button>
                    <button v-if="canCancelRow(item)"
                            @click="openQuickCancel(item)"
                            class="btn btn-sm btn-outline-danger is-border-1 ml-1 mb-1">
                      <font-awesome-icon icon="times-circle" />&nbsp;{{ $t('kycRequest.action.cancel') }}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="col-12 text-center text-danger" v-if="!items.length && !loading">
        <span v-if="filterFormModel.page === 1 && !filterFormModel.q">{{ $t('notFoundCreate') }}</span>
        <span v-else>{{ $t('notFoundSearch') }}</span>
      </div>

      <div class="col-12" v-if="itemCount">
        <div class="c-paging__count" v-if="itemCount">{{ $t('found', [itemCount]) }}</div>
        <b-pagination v-if="itemCount > filterFormModel.pageSize" class="pull-right" size="sm"
                      v-model="filterFormModel.page" :total-rows="itemCount"
                      :per-page="filterFormModel.pageSize" @input="load" />
      </div>
    </div>

    <k-y-c-request-create-modal ref="createModal" @created="onCreated" @open-existing="openDetail" />
    <k-y-c-request-detail-modal ref="detailModal" @updated="onDetailUpdated" />

    <!-- Quick cancel modal -->
    <b-modal v-model="cancelShow" :title="$t('kycRequest.action.cancel')" hide-footer>
      <p v-if="cancelTarget" class="text-muted small mb-2">
        Yêu cầu của khách
        <strong>{{ cancelTarget.client ? (cancelTarget.client.companyName || cancelTarget.client.domain) : '—' }}</strong>
      </p>
      <b-form @submit.prevent="doQuickCancel">
        <b-form-group :label="$t('kycRequest.field.cancelReason') + ' *'">
          <b-form-textarea v-model="cancelReason" rows="3" maxlength="500" required />
        </b-form-group>
        <div class="text-right">
          <b-button @click="cancelShow = false" variant="outline-secondary" class="is-border-1 mr-2">
            {{ $t('cancel') }}
          </b-button>
          <b-button type="submit" variant="danger" :disabled="!cancelReason.trim() || canceling">
            <b-spinner small v-if="canceling" />
            <font-awesome-icon v-else icon="times-circle" />
            &nbsp;{{ $t('kycRequest.action.cancel') }}
          </b-button>
        </div>
      </b-form>
    </b-modal>
  </main>
</template>

<script>
import List from '@/mixins/List';
import TimeRangePicker from '@/components/TimeRangePicker.vue';
import AdminUserSelect from '@/components/AdminUserSelect.vue';
import ClientSelect from '@/components/ClientSelect.vue';
import KYCRequestStatusBadge from './KYCRequestStatusBadge.vue';
import KYCRequestCreateModal from './KYCRequestCreateModal.vue';
import KYCRequestDetailModal from './KYCRequestDetailModal.vue';
import moment from 'moment';

const ROLE_CREATE = ['owner','admin','tech','operationsManager','sales','salesAssistant','account','accountant','am','manager','supervisor'];

export default {
  components: { TimeRangePicker, AdminUserSelect, ClientSelect, KYCRequestStatusBadge, KYCRequestCreateModal, KYCRequestDetailModal },
  mixins: [List],
  data() {
    return {
      service: '/kyc-request',
      reloadBus: 'reloadKYCRequest',
      wesignSyncing: {},  // {kycId: true}
      defaultFilterFormModel: {
        page: 1,
        pageSize: 50,
        q: null,
        client: null,
        status: null,
        contractType: null,
        priority: null,
        assignedTo: null,
        requestedBy: null,
      },
      stats: null,
      cancelShow: false,
      cancelTarget: null,
      cancelReason: '',
      canceling: false,
      statusTabs: [
        { value: null, label: this.$t('kycRequest.status.all'), icon: null },
        { value: 'pending', label: this.$t('kycRequest.status.pending'), icon: 'clock' },
        { value: 'waitingKyc', label: this.$t('kycRequest.status.waitingKyc'), icon: 'hourglass-half' },
        { value: 'kycSubmitted', label: this.$t('kycRequest.status.kycSubmitted'), icon: 'check-circle' },
        { value: 'inReview', label: this.$t('kycRequest.status.inReview'), icon: 'eye' },
        { value: 'exported', label: this.$t('kycRequest.status.exported'), icon: 'file-pdf' },
        { value: 'canceled', label: this.$t('kycRequest.status.canceled'), icon: 'times-circle' },
      ],
      contractTypeOptions: [
        { value: null, text: this.$t('kycRequest.field.contractType') },
        { value: 'new', text: this.$t('kycRequest.contractType.new') },
        { value: 'renewal', text: this.$t('kycRequest.contractType.renewal') },
        { value: 'upgrade', text: this.$t('kycRequest.contractType.upgrade') },
        { value: 'changeEntity', text: this.$t('kycRequest.contractType.changeEntity') },
        { value: 'other', text: this.$t('kycRequest.contractType.other') },
      ],
      priorityOptions: [
        { value: null, text: this.$t('kycRequest.field.priority') },
        { value: 'normal', text: this.$t('kycRequest.priority.normal') },
        { value: 'high', text: this.$t('kycRequest.priority.high') },
        { value: 'urgent', text: this.$t('kycRequest.priority.urgent') },
      ],
    };
  },
  computed: {
    canCreate() {
      const u = this.$store.state.user;
      return u && ROLE_CREATE.indexOf(u.role) >= 0;
    },
  },
  methods: {
    loadStats() {
      this.$bgService.get('/kyc-request/stats').then(r => {
        this.stats = r.data;
      }).catch(() => { /* silent */ });
    },
    onCreated() {
      this.load();
      this.loadStats();
    },
    openDetail(id) {
      this.$refs.detailModal.open(id);
    },
    onDetailUpdated() {
      this.load();
      this.loadStats();
    },
    canCancelRow(item) {
      if (!item) return false;
      if (['exported','canceled'].indexOf(item.status) >= 0) return false;
      const u = this.$store.state.user;
      if (!u) return false;
      // Role thuộc nhóm xử lý (admin/owner + Account/CS team + manager) act-all được.
      const ACT_ALL_ROLES = ['owner','admin','tech','operationsManager','account','accountant','am','manager','supervisor'];
      if (ACT_ALL_ROLES.indexOf(u.role) >= 0) return true;
      // Creator
      const requesterId = item.requestedBy && (item.requestedBy._id || item.requestedBy);
      if (requesterId && requesterId.toString() === u._id.toString()) return true;
      // Assigned
      const assignedId = item.assignedTo && (item.assignedTo._id || item.assignedTo);
      if (assignedId && assignedId.toString() === u._id.toString()) return true;
      return false;
    },
    openQuickCancel(item) {
      this.cancelTarget = item;
      this.cancelReason = '';
      this.cancelShow = true;
    },
    async doQuickCancel() {
      if (!this.cancelTarget || !this.cancelReason.trim()) return;
      this.canceling = true;
      try {
        await this.$service.post('/kyc-request/' + this.cancelTarget._id + '/cancel', {
          reason: this.cancelReason.trim(),
        });
        this.$swal({text: this.$t('success'), icon: 'success', timer: 1500});
        this.cancelShow = false;
        this.cancelTarget = null;
        this.cancelReason = '';
        this.load();
        this.loadStats();
      } catch (e) {
        const msg = (e.response && e.response.data && e.response.data.message) || 'Cancel failed';
        this.$swal({text: msg, icon: 'error'});
      } finally {
        this.canceling = false;
      }
    },
    changeStatus(value) {
      this.filterFormModel.status = value;
      this.filterFormModel.page = 1;
      this.load();
    },
    tabCount(value) {
      if (!this.stats || !this.stats.byStatus) return '';
      if (!value) {
        let total = 0;
        Object.keys(this.stats.byStatus).forEach(k => { total += this.stats.byStatus[k] || 0; });
        return total;
      }
      return this.stats.byStatus[value] || 0;
    },
    formatTimeAgo(t) {
      if (!t) return '';
      const diff = moment().diff(moment(parseInt(t)));
      const minutes = Math.floor(diff / 60000);
      if (minutes < 1) return 'vừa xong';
      if (minutes < 60) return minutes + 'p trước';
      const hours = Math.floor(minutes / 60);
      if (hours < 24) return hours + 'h trước';
      const days = Math.floor(hours / 24);
      if (days < 30) return days + ' ngày trước';
      return moment(parseInt(t)).format('DD/MM/YYYY');
    },
    formatTimeAgoShort(t) {
      if (!t) return '';
      const m = Math.floor(moment().diff(moment(t)) / 60000);
      if (m < 1) return 'vừa xong';
      if (m < 60) return m + 'p trước';
      const h = Math.floor(m / 60);
      if (h < 24) return h + 'h trước';
      return Math.floor(h / 24) + ' ngày trước';
    },
    // ===== MISA WeSign helpers (copy từ KYCList.vue) =====
    wesignStatusLabel(s) {
      const m = {pending: 'Chờ ký', inProgress: 'Đang ký', completed: 'Đã ký xong', rejected: 'Khách từ chối', cancelled: 'Đã huỷ', expired: 'Hết hạn'};
      return m[s] || s;
    },
    wesignBadgeClass(s) {
      const m = {pending: 'badge-secondary', inProgress: 'badge-info', completed: 'badge-success', rejected: 'badge-danger', cancelled: 'badge-warning', expired: 'badge-dark'};
      return m[s] || 'badge-light';
    },
    isInternalParticipant(p) {
      return !!(p && p.email && p.email.toLowerCase().includes('@gadget.com.vn'));
    },
    customerParticipants(kyc) {
      return (kyc && kyc.wesignParticipants || []).filter(p => !this.isInternalParticipant(p));
    },
    customerSignedCount(kyc) {
      return this.customerParticipants(kyc).filter(p => p.action === 2).length;
    },
    customerActionLabel(action) {
      const m = {0: '⏳ Chưa nhận', 1: '⏳ Đang chờ ký', 2: '✓ Đã ký', 3: '✗ Từ chối'};
      return m[action] || '?';
    },
    customerActionClass(action) {
      if (action === 2) return 'text-success';
      if (action === 3) return 'text-danger';
      if (action === 1) return 'text-info';
      return 'text-muted';
    },
    async onSyncWesignKyc(item) {
      if (!item.kyc || !item.kyc._id) return;
      this.$set(this.wesignSyncing, item.kyc._id, true);
      try {
        const res = await this.$service.post(`/client-kyc/${item.kyc._id}/sync-wesign-ref`);
        if (res.data && res.data.success) {
          this.$set(item.kyc, 'wesignStatus', res.data.wesignStatus);
          this.$set(item.kyc, 'wesignDocumentName', res.data.wesignDocumentName);
          this.$set(item.kyc, 'wesignParticipants', res.data.wesignParticipants || []);
          this.$set(item.kyc, 'wesignLastSyncTime', Date.now());
          this.$swal({title: 'Đã đồng bộ', icon: 'success', toast: true, position: 'top-end', timer: 1500, showConfirmButton: false});
        }
      } catch (error) {
        const msg = error.response && error.response.data && error.response.data.message;
        this.$swal({text: msg || 'Đồng bộ thất bại', icon: 'error'});
      } finally {
        this.$set(this.wesignSyncing, item.kyc._id, false);
      }
    },
    priorityBadgeClass(p) {
      const map = {normal: 'badge badge-light', high: 'badge badge-warning', urgent: 'badge badge-danger'};
      return map[p] || 'badge badge-light';
    },
    // Đánh dấu request quá 7 ngày chưa terminal — bù cho việc bỏ cron-stale Slack
    isStale(item) {
      if (!item || !item.createTime) return false;
      if (['exported', 'canceled'].indexOf(item.status) >= 0) return false;
      return (Date.now() - parseInt(item.createTime)) > 7 * 24 * 3600 * 1000;
    },
  },
  mounted() {
    this.load();
    this.loadStats();
    // Auto-open detail nếu có query ?open=<id> (từ "Mở yêu cầu hiện có")
    const openId = this.$route.query.open;
    if (openId) {
      this.$nextTick(() => this.openDetail(openId));
      // Xoá query để khỏi auto-open lại khi user F5
      const q = {...this.$route.query};
      delete q.open;
      this.$router.replace({query: q}).catch(() => {});
    }
  },
  i18n: {
    messages: {
      en: {
        detail: 'Detail',
      },
      vi: {
        detail: 'Chi tiết',
      },
    },
  },
};
</script>

<style scoped>
.kyc-avatar {
  width: 40px; height: 40px; border-radius: 50%;
  display: flex; align-items: center; justify-content: center;
  color: #fff; font-size: 18px; flex-shrink: 0;
}
.kyc-avatar--enterprise { background: #6c5ce7; }
.note-truncate {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
.domain-tag {
  background: #f1f3f5; color: #495057; padding: 1px 6px;
  border-radius: 3px; font-size: 12px;
}
.min-width-0 { min-width: 0; }
.kyc-status-tabs { border-bottom: 2px solid #e0e6ed; flex-wrap: wrap; }
.kyc-status-tabs .nav-link {
  color: #495057; padding: 8px 14px; border: 0;
  border-bottom: 2px solid transparent; margin-bottom: -2px;
  font-size: 13px;
}
.kyc-status-tabs .nav-link:hover { color: #1e90ff; background: transparent; }
.kyc-status-tabs .nav-link.active {
  color: #1e90ff; border-bottom-color: #1e90ff; background: transparent;
  font-weight: 600;
}
/* ===== MISA WeSign box (copy từ KYCList.vue) ===== */
.wesign-box {
  background: #f0f9ff; border: 1px solid #d1ecf9; border-left: 3px solid #17a2b8;
  border-radius: 4px; padding: 6px 8px; font-size: 12px;
}
.wesign-row { display: flex; justify-content: space-between; align-items: center; padding: 2px 0; }
.wesign-header { border-bottom: 1px dashed #cce5f1; padding-bottom: 4px; margin-bottom: 4px; }
.wesign-footer { border-top: 1px dashed #cce5f1; padding-top: 4px; margin-top: 4px; font-size: 11px; }
.wesign-doc-name { margin-bottom: 4px; color: #495057; }
.wesign-participants { width: 100%; border-collapse: collapse; margin: 0; }
.wesign-participants td { padding: 2px 4px; vertical-align: middle; border-bottom: 1px solid rgba(0,0,0,0.04); }
.wesign-participants tr:last-child td { border-bottom: none; }
.wesign-participants .w-icon { width: 18px; text-align: center; }
.wesign-participants .w-name { font-weight: 500; white-space: nowrap; max-width: 130px; overflow: hidden; text-overflow: ellipsis; }
.wesign-participants .w-role { width: 40px; text-align: center; }
.wesign-participants .w-email { font-size: 11px; max-width: 0; }
.wesign-box code { background: rgba(0,0,0,0.04); padding: 1px 4px; border-radius: 2px; font-size: 10px; }
.wesign-box .badge { font-size: 10px; }
.wesign-icon-btn {
  display: inline-flex; align-items: center; justify-content: center;
  width: 22px; height: 22px; border-radius: 50%; border: 1px solid transparent;
  background: white; font-size: 11px; margin-left: 4px; transition: all 0.15s;
  text-decoration: none !important;
}
.wesign-icon-btn:hover { transform: scale(1.08); text-decoration: none; }
.wesign-icon-btn--info { color: #17a2b8; border-color: #17a2b8; }
.wesign-icon-btn--info:hover { background: #17a2b8; color: white; }
.wesign-icon-btn--primary { color: #007bff; border-color: #007bff; }
.wesign-icon-btn--primary:hover { background: #007bff; color: white; }
.wesign-icon-btn--disabled { pointer-events: none; opacity: 0.5; }
</style>
